import { useForm, Controller } from "react-hook-form";
import { useEffect, useMemo, useState } from "react";
import { useSetAtom } from "jotai";
import { setModalCloseAtom } from "atoms/modalAtom/modalAtom";
import { useItemGroups } from "atoms/itemsGropAtom";
import { ItemsGroup } from "types";
import { allItemGroups } from "constant-data/items";

import { BaseLayout } from "components/BaseLayout/BaseLayout";
import { ModalHeader } from "components/Modal/AppModal";
import { Button, EButtonVariant } from "components/Button/Button";
import { NamedInput } from "components/NamedInput/NamedInput";
import { NamedCheckbox } from "components/NamedCheckbox/NamedCheckbox";
import { AppSelect, Option } from "components/Select/Select";
import { VerticalSpace } from "components/VerticalSpace/VerticalSpace";
import { HorizontalSpace } from "components/HorizontalSpace/HorizontalSpace";

import styles from "../styles.module.scss";
import {
  useAddProductsGroup,
  useEditProductsGroup,
  useGetProductsGroup,
} from "../api";

export type FormItemsGroupValues = {
  name: string;
  code: string;
  groupPosition: number | "";
  selectedOption: Option;
  groupid?: string;
};

type SettingsModalProps = {
  isEdit: boolean;
};

const staticOption: Option = {
  value: allItemGroups.itemgroupid,
  label: allItemGroups.name,
};

export const SettingsModal = ({ isEdit }: SettingsModalProps): JSX.Element => {
  const { itemGroupsState, setItemGroupsState } = useItemGroups();

  const {
    handleSubmit,
    control,
    setValue,
    formState: { errors },
    clearErrors,
    reset,
  } = useForm<FormItemsGroupValues>({
    defaultValues: {
      selectedOption: staticOption,
      groupPosition: 0,
      groupid: "",
      name: "",
      code: "",
    },
  });

  const closeModal = useSetAtom(setModalCloseAtom);
  const [isAvailable, setIsAvailable] = useState(false);
  const [isOnlywhole, setIsOnlywhole] = useState(false);

  const { data: items = [] } = useGetProductsGroup({ enabled: false });

  const { mutate: addProductGroup } = useAddProductsGroup();

  const { mutate: editProductGroup } = useEditProductsGroup();

  const options: Option[] = useMemo(
    () => [
      staticOption,
      ...items.map((item: ItemsGroup) => ({
        value: item?.itemgroupid || "",
        label: item?.name || "",
      })),
    ],
    [items]
  );

  const handleSave = async (data: FormItemsGroupValues) => {
    const addParams = {
      name: data.name,
      parentid: data.selectedOption.value,
      importcode: data.code,
      position: typeof data.groupPosition === "number" ? data.groupPosition : 0,
      avaliable: isAvailable ? 1 : 0,
      onlywhole: isOnlywhole ? 1 : 0,
    };

    const editParams = {
      groupid: itemGroupsState.activeItemsGroup?.itemgroupid,
      name: data.name,
      parentid: data.selectedOption.value,
      position: typeof data.groupPosition === "number" ? data.groupPosition : 0,
      avaliable: isAvailable ? 1 : 0,
      onlywhole: isOnlywhole ? 1 : 0,
    };

    if (isEdit) {
      editProductGroup(editParams, {
        onSuccess: () => {
          setItemGroupsState((prev) => ({
            ...prev,
            activeItemsGroup: prev.activeItemsGroup
              ? {
                  ...prev.activeItemsGroup,
                  name: editParams.name,
                  parentid: editParams.parentid,
                  position: editParams.position,
                  avaliable: editParams.avaliable,
                  onlywhole: editParams.onlywhole,
                }
              : null,
          }));
          closeModal();
        },
      });
    } else {
      addProductGroup(addParams);
      closeModal();
    }
  };

  useEffect(() => {
    const { activeItemsGroup } = itemGroupsState;

    if (activeItemsGroup && isEdit) {
      const activeItemsGroupOption = options.find(
        (option) => option.value === activeItemsGroup.parentid
      );

      reset({
        name: activeItemsGroup.name || "",
        groupPosition: activeItemsGroup.position ?? "",
        groupid: activeItemsGroup.itemgroupid,
        selectedOption: activeItemsGroupOption,
      });

      setIsAvailable(!!activeItemsGroup.avaliable);
      setIsOnlywhole(!!activeItemsGroup.onlywhole);
    } else if (activeItemsGroup) {
      const activeItemsGroupOption = options.find(
        (option) => option.value === activeItemsGroup.itemgroupid
      )!;

      setValue("selectedOption", activeItemsGroupOption);
    }
  }, [itemGroupsState.activeItemsGroup, options, setValue]);

  return (
    <BaseLayout
      // width={400}
      // height={400}
      className={styles.settingsModal}
      header={
        <ModalHeader
          title={itemGroupsState?.activeItemsGroup?.name || "Новая группа"}
        />
      }
      footer={
        <Button
          variant={EButtonVariant.SECONDARY}
          onClick={handleSubmit(handleSave)}
          icon="galochka"
        >
          Сохранить
        </Button>
      }
    >
      <div className={styles.settingsBody}>
        <VerticalSpace size={15} />
        {isEdit && (
          <>
            <div className="flex items-center">
              <Controller
                name="groupid"
                control={control}
                render={({ field }) => (
                  <NamedInput
                    {...field}
                    label="ID"
                    placeholder="4"
                    type="text"
                    disabled
                    onChange={(e) => {
                      field.onChange(e);
                    }}
                  />
                )}
              />
            </div>
            <VerticalSpace size={25} />
          </>
        )}
        <div className="flex items-center">
          <Controller
            name="selectedOption"
            control={control}
            render={({ field }) => (
              <AppSelect
                {...field}
                label="В группе:"
                options={options}
                className={styles.select}
                placeholder="Выберите опцию"
                isDisabled={isEdit}
                onChange={(option) => {
                  field.onChange(option);
                  clearErrors("selectedOption");
                }}
              />
            )}
          />
        </div>
        <VerticalSpace size={25} />
        <div className="flex items-center">
          <Controller
            name="name"
            control={control}
            rules={{ required: "Наименование обязательно" }}
            render={({ field }) => (
              <NamedInput
                {...field}
                label="Наименование"
                placeholder="Горшечка"
                type="text"
                isRequired
                hasError={!!errors.name}
                errorMessage={errors.name?.message}
                onChange={(e) => {
                  field.onChange(e);
                  clearErrors("name");
                }}
              />
            )}
          />
        </div>
        <VerticalSpace size={25} />
        <div className="flex items-center">
          <Controller
            name="groupPosition"
            control={control}
            render={({ field }) => (
              <NamedInput
                {...field}
                label="Позиция группы"
                placeholder="0"
                type="number"
                onChange={(e) => {
                  const target = e.target as HTMLInputElement;
                  const value = parseFloat(target.value);
                  field.onChange(e);
                  setValue("groupPosition", isNaN(value) ? "" : value);
                }}
              />
            )}
          />
        </div>
        {/*<VerticalSpace size={30} />
        <div className="flex items-center">
          <NamedCheckbox
            name="Доступен в терминале"
            checked={isAvailable}
            onChange={(checked) => setIsAvailable(checked)}
          />
          <HorizontalSpace size={24} />
          <NamedCheckbox
            name="Продавать только целое количество"
            checked={isOnlywhole}
            onChange={(checked) => setIsOnlywhole(checked)}
          />
        </div>*/}
      </div>
    </BaseLayout>
  );
};
